import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MARKETPLACE_SEO, buildCanonical } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { getMarketplaceStoreBySlug, getMarketplaceStoreProducts } from "../../services/marketplaceApi";
import { getPreviewStoreBySlug } from "../../data/marketplacePreviewStores";
import { previewStoreBannerImage, resolveApiStoreBanner, resolveProductVisual } from "../../data/marketplaceVisualAssets";
import "./marketplace.css";

function productThumb(p) {
  const url = p.thumbnailUrl || (Array.isArray(p.previewImages) && p.previewImages[0]) || "";
  const src = url || resolveProductVisual(p);
  return <img className="mp-card__thumb" src={src} alt="" loading="lazy" />;
}

const DUMMY_OFFERINGS = [
  {
    title: "Founders bundle",
    tag: "Digital",
    blurb: "Curated drops aligned to your storefront theme when publishing opens.",
  },
  {
    title: "Season pass",
    tag: "Access",
    blurb: "Future SKUs and lore packs staged for the first live season.",
  },
  {
    title: "Creator collab",
    tag: "Limited",
    blurb: "Cross-promo slots for verified Oshar partners — offline until go-live.",
  },
];

function offeringCaseFromTitle(title, idx) {
  const tags = ["Showcase", "Season lane", "Vault row"];
  const blurbs = [
    "Sealed display case — commerce offline.",
    "Awaiting seller publishing window.",
    "Preview merchandising slot — not for sale.",
  ];
  return { title, tag: tags[idx % tags.length], blurb: blurbs[idx % blurbs.length] };
}

export default function MarketplaceStorePage() {
  const { storeSlug } = useParams();
  const [store, setStore] = useState(null);
  const [preview, setPreview] = useState(false);
  const [products, setProducts] = useState([]);
  const [fixture, setFixture] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const previewFixture = useMemo(() => getPreviewStoreBySlug(storeSlug), [storeSlug]);

  const pageSeo = useMemo(() => {
    const name = store?.name || previewFixture?.name || "Storefront";
    const path = `/marketplace/store/${storeSlug}`;
    return {
      ...MARKETPLACE_SEO,
      title: `${name} | Treydora Marketplace Preview`,
      description:
        store?.tagline ||
        previewFixture?.tagline ||
        MARKETPLACE_SEO.description,
      canonicalUrl: buildCanonical(path),
      ogUrl: buildCanonical(path),
    };
  }, [store, previewFixture, storeSlug]);

  usePageSeo(pageSeo);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      setFixture(null);
      try {
        const data = await getMarketplaceStoreBySlug(storeSlug);
        const s = data?.store;
        if (!s) {
          throw new Error("Store not found.");
        }
        if (!cancelled) {
          setStore(s);
          setPreview(Boolean(data?.preview));
        }
        let rows = [];
        try {
          rows = await getMarketplaceStoreProducts(storeSlug);
        } catch {
          rows = [];
        }
        if (!cancelled) {
          setProducts(Array.isArray(rows) ? rows : []);
        }
      } catch (e) {
        if (!cancelled) {
          setStore(null);
          setProducts([]);
          setPreview(false);
          if (previewFixture) {
            setFixture(previewFixture);
            setErr("");
          } else {
            setErr(e?.message || "Unable to load store.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeSlug, previewFixture]);

  if (loading) {
    return (
      <div className="mp-page mp-page--store-detail mp-atmosphere">
        <p className="mp-muted">Loading storefront…</p>
      </div>
    );
  }

  if (err && !fixture) {
    return (
      <div className="mp-page mp-atmosphere">
        <p className="mp-banner mp-banner--warn">{err}</p>
        <Link to="/marketplace" className="mp-link mp-back-link">
          ← Oshar Marketplace
        </Link>
      </div>
    );
  }

  const isFixture = Boolean(fixture) && !store;
  const heroTitle = store?.storeName || fixture?.storeName;
  const heroDesc = store?.description || fixture?.shortDescription;
  const district = fixture?.district || (Array.isArray(store?.categories) && store.categories[0]) || "Oshar parcel";
  const districtTheme = fixture?.districtTheme || "Verified Oshar parcel";
  const propertyClass =
    fixture?.propertyClass ||
    fixture?.propertyType ||
    (store ? `VR Real Estate · token ${store.realEstateTokenId}` : "Linked NFT parcel");
  const categoryLabel = fixture?.categoryLabel || "Storefront";
  const operatorName = fixture?.operatorName || "Verified operator (preview)";
  const heroTagline = fixture?.heroTagline || "";
  const productSlots = fixture?.productSlotCount ?? 12;
  const commercialStatus = isFixture ? "Preview fixture" : preview ? "Owner preview" : "Commerce offline";

  const heroGradientClass =
    isFixture && fixture?.detailAccent
      ? `mp-store-detail-hero--accent-${fixture.detailAccent}`
      : "mp-store-detail-hero--default";

  const bannerVariant = fixture?.bannerVariant || "ember";

  return (
    <div className="mp-page mp-page--store-detail mp-atmosphere">
      {preview && store ? (
        <div className="mp-banner mp-banner--warn" style={{ marginBottom: "1rem" }}>
          Preview only — this storefront is not public yet. Sign in as the owner to view while it is draft or pending
          approval.
        </div>
      ) : null}

      {isFixture ? (
        <div className="mp-preview-ribbon mp-preview-ribbon--tight mp-preview-ribbon--elevated" role="status">
          <span className="mp-preview-ribbon__badge">Marketplace Preview</span>
          <span>Visual fixture — <strong>not</strong> a live seller. No purchases or contact forms.</span>
        </div>
      ) : (
        <div className="mp-preview-ribbon mp-preview-ribbon--tight mp-preview-ribbon--info mp-preview-ribbon--elevated" role="status">
          <span className="mp-preview-ribbon__badge">Visual phase</span>
          <span>
            Live storefront record from the API. <strong>Purchases and contact remain disabled</strong> on Treydora.com
            until operations go live.
          </span>
        </div>
      )}

      <header className={`mp-store-detail-hero mp-store-detail-hero--enter mp-glass ${heroGradientClass}`}>
        <div className="mp-store-detail-hero__banner" aria-hidden>
          {store?.bannerUrl ? (
            <img className="mp-store-detail-hero__img" src={store.bannerUrl} alt="" />
          ) : isFixture ? (
            <>
              <img className="mp-store-detail-hero__img" src={previewStoreBannerImage(fixture?.categoryId)} alt="" />
              <div className="mp-store-detail-hero__banner-toning" aria-hidden />
            </>
          ) : store ? (
            <>
              <img className="mp-store-detail-hero__img" src={resolveApiStoreBanner(store)} alt="" />
              <div className="mp-store-detail-hero__banner-toning" aria-hidden />
            </>
          ) : (
            <div className={`mp-store-detail-hero__synth mp-store-detail-hero__synth--${bannerVariant}`}>
              <span className="mp-store-detail-hero__synth-icon" aria-hidden>
                {fixture?.iconLabel || "◆"}
              </span>
            </div>
          )}
          <div className="mp-store-detail-hero__banner-mist" aria-hidden />
        </div>
        <div className="mp-store-detail-hero__inner">
          <div className="mp-store-detail-hero__titleblock">
            <div className="mp-store-detail-badges">
              <span className="mp-badge mp-badge--nft">NFT Real Estate Storefront</span>
              <span className="mp-badge mp-badge--preview">Marketplace Preview</span>
              {isFixture ? (
                <span className="mp-badge mp-badge--soon">Coming Soon</span>
              ) : preview ? (
                <span className="mp-badge mp-badge--soon">Owner preview</span>
              ) : (
                <span className="mp-badge mp-badge--soon">Commerce offline</span>
              )}
            </div>
            <p className="mp-hero__eyebrow mp-hero__eyebrow--gold">{districtTheme}</p>
            <h1 className="mp-hub-hero__title mp-store-detail-hero__h1">{heroTitle}</h1>
            {heroTagline ? <p className="mp-store-detail-hero__tagline">{heroTagline}</p> : null}
            <p className="mp-store-detail__catline">
              <span>{categoryLabel}</span>
              <span className="mp-store-detail__dot" aria-hidden />
              <span>{propertyClass}</span>
            </p>
            <p className="mp-store-detail__operator">
              <span className="mp-store-detail__operator-label">Operator</span> <strong>{operatorName}</strong>
            </p>
            <p className="mp-hub-hero__sub mp-store-detail__desc">{heroDesc}</p>
            <div className="mp-store-detail__powered-wrap">
              <span className="mp-sf-card__nft-icon" aria-hidden />
              <p className="mp-store-detail__powered">Powered by Treydora VR Real Estate NFTs</p>
            </div>
          </div>
        </div>
      </header>

      <section className="mp-sec">
        <h2 className="mp-sec__title mp-sec__title--console">Property terminal</h2>
        <p className="mp-muted mp-sec__lede">Parcel telemetry — read-only during preview.</p>
        <div className="mp-stats-grid mp-stats-grid--terminal">
          <div className="mp-stat-tile mp-stat-tile--terminal mp-glass">
            <span className="mp-stat-tile__glyph" aria-hidden>
              ⧉
            </span>
            <span className="mp-stat-tile__label">Property class</span>
            <span className="mp-stat-tile__value">{propertyClass}</span>
          </div>
          <div className="mp-stat-tile mp-stat-tile--terminal mp-glass">
            <span className="mp-stat-tile__glyph" aria-hidden>
              ⌖
            </span>
            <span className="mp-stat-tile__label">District</span>
            <span className="mp-stat-tile__value">{district}</span>
          </div>
          <div className="mp-stat-tile mp-stat-tile--terminal mp-glass">
            <span className="mp-stat-tile__glyph" aria-hidden>
              ⏣
            </span>
            <span className="mp-stat-tile__label">Future product slots</span>
            <span className="mp-stat-tile__value">{productSlots} planned</span>
          </div>
          <div className="mp-stat-tile mp-stat-tile--terminal mp-glass">
            <span className="mp-stat-tile__glyph" aria-hidden>
              ⎋
            </span>
            <span className="mp-stat-tile__label">Commercial status</span>
            <span className="mp-stat-tile__value">{commercialStatus}</span>
          </div>
        </div>
      </section>

      <section className="mp-sec">
        <h2 className="mp-sec__title mp-sec__title--console">
          {products.length > 0 ? "Published listings" : "Featured offerings"}
        </h2>
        <p className="mp-muted mp-sec__lede">
          {products.length > 0 ? "Browse-only — purchases stay offline." : "Display cases — preview lanes only."}
        </p>
        {products.length === 0 ? (
          <div className="mp-grid mp-grid--offerings">
            {(isFixture && fixture?.offerings ? fixture.offerings : []).map((title, idx) => {
              const c = offeringCaseFromTitle(title, idx);
              return (
                <div key={title} className="mp-offering-case mp-glass">
                  <div className={`mp-offering-case__header mp-offering-case__header--${bannerVariant}`}>
                    <span className="mp-offering-case__glow" aria-hidden />
                  </div>
                  <div className="mp-offering-case__body">
                    <span className="mp-offering-case__type">{c.tag}</span>
                    <h3>{c.title}</h3>
                    <p className="mp-muted">{c.blurb}</p>
                    <span className="mp-offering-case__locked">Locked · Coming Soon</span>
                  </div>
                </div>
              );
            })}
            {(!isFixture || !fixture?.offerings?.length ? DUMMY_OFFERINGS : []).map((o, idx) => (
              <div key={o.title} className="mp-offering-case mp-glass">
                <div className={`mp-offering-case__header mp-offering-case__header--${["ember", "violet", "cyan"][idx % 3]}`}>
                  <span className="mp-offering-case__glow" aria-hidden />
                </div>
                <div className="mp-offering-case__body">
                  <span className="mp-offering-case__type">{o.tag}</span>
                  <h3>{o.title}</h3>
                  <p className="mp-muted">{o.blurb}</p>
                  <span className="mp-offering-case__locked">Locked · Coming Soon</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mp-grid mp-grid--catalog">
            {products.map((p) => (
              <Link
                key={p._id}
                className="mp-card mp-card--catalog mp-glass"
                to={`/marketplace/product/${encodeURIComponent(p.slug)}?storeSlug=${encodeURIComponent(store.storeSlug)}`}
              >
                {productThumb(p)}
                {p.category ? <p className="mp-card__meta">{p.category}</p> : null}
                <h3>{p.title}</h3>
                <p className="mp-price">${(Number(p.priceCents || 0) / 100).toFixed(2)} USD</p>
                <p className="mp-muted mp-card__hint">Viewing only — purchases coming soon.</p>
              </Link>
            ))}
          </div>
        )}
      </section>

      <div className="mp-store-detail-actions">
        <button type="button" className="mp-btn mp-btn--primary" disabled title="Commerce is not live in this phase">
          Commerce Offline
        </button>
        <button type="button" className="mp-btn mp-btn--ghost" disabled title="Contact is not enabled in this phase">
          Store Contact Locked
        </button>
      </div>

      <Link to="/marketplace" className="mp-link mp-back-link">
        ← Back to Oshar Marketplace
      </Link>
    </div>
  );
}
